<script setup lang="ts">
import {
  PhArrowLeft as ArrowLeftIcon,
  PhCalendarBlank as CalendarBlankIcon,
  PhCheckCircle as CheckCircleIcon,
  PhFileText as FileTextIcon,
  PhShieldCheck as ShieldCheckIcon,
  PhUploadSimple as UploadSimpleIcon,
  PhWarningCircle as WarningCircleIcon,
} from '@phosphor-icons/vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import OrderTimeline from '../components/OrderTimeline.vue'
import StatusState from '../components/StatusState.vue'
import { ApiError, api } from '../services/api'
import { useSessionStore } from '../stores/session'
import type { CustomerOrder } from '../types'

const route = useRoute()
const session = useSessionStore()
const order = ref<CustomerOrder | null>(null)
const loading = ref(true)
const error = ref('')
const activeSection = ref('timeline')
const detailTabsRef = ref<HTMLElement | null>(null)
const detailTabsScrollable = ref(false)
const confirmationCode = ref('')
const confirmationCodeSent = ref(false)
const confirmationSending = ref(false)
const confirmationSmsMessage = ref('')
const confirming = ref(false)
const confirmMessage = ref('')
const viewedConfirmationSectionIds = ref<string[]>([])
const actionError = ref('')
const paymentFile = ref<File | null>(null)
const payerName = ref('')
const bankReference = ref('')
const paymentAmountYuan = ref('')
const uploading = ref(false)
const uploadMessage = ref('')
const bookingSubmitting = ref(false)
const bookingMessage = ref('')
const bookingError = ref('')
const surveyBooking = ref({
  province: '',
  city: '',
  district: '',
  detail: '',
  requestedSlot: '',
})

const viewedConfirmationSectionCount = computed(() => {
  const currentSectionIds = new Set(
    order.value?.confirmation.sections.map((section) => section.id) ?? [],
  )
  return viewedConfirmationSectionIds.value.filter((id) => currentSectionIds.has(id)).length
})
const hasReviewedAllConfirmationSections = computed(
  () =>
    order.value?.confirmation.sections.length === 4 &&
    viewedConfirmationSectionCount.value === 4,
)
const canConfirm = computed(
  () =>
    hasReviewedAllConfirmationSections.value &&
    confirmationCodeSent.value &&
    order.value?.confirmation.status === 'waiting' &&
    /^\d{6}$/.test(confirmationCode.value),
)
const pendingMilestone = computed(() =>
  order.value?.payments.find((payment) => payment.status === 'waiting'),
)
const canCreateSurveyBooking = computed(
  () => order.value?.inquiryStatus === 'accepted' && !order.value.survey.hasBooking,
)

let detailTabsResizeObserver: ResizeObserver | null = null

function updateDetailTabsScrollability() {
  const element = detailTabsRef.value
  detailTabsScrollable.value = Boolean(
    element && element.scrollWidth > element.clientWidth + 1,
  )
}

function observeDetailTabs(element: HTMLElement | null) {
  detailTabsResizeObserver?.disconnect()
  detailTabsResizeObserver = null
  detailTabsScrollable.value = false
  if (!element) return

  void nextTick(() => {
    if (detailTabsRef.value !== element) return
    updateDetailTabsScrollability()
    if (typeof ResizeObserver !== 'undefined') {
      detailTabsResizeObserver = new ResizeObserver(updateDetailTabsScrollability)
      detailTabsResizeObserver.observe(element)
    }
  })
}

async function loadOrder() {
  loading.value = true
  error.value = ''
  viewedConfirmationSectionIds.value = []
  confirmationCode.value = ''
  confirmationCodeSent.value = false
  confirmationSmsMessage.value = ''
  try {
    order.value = await api.getOrder(String(route.params.id))
    const due = order.value.payments.find((payment) => payment.status === 'waiting')?.amountDueCents
    paymentAmountYuan.value = typeof due === 'number' && due > 0 ? (due / 100).toFixed(2) : ''
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '订单加载失败'
  } finally {
    loading.value = false
  }
}

function markConfirmationSectionViewed(sectionId: string, event: Event) {
  const details = event.currentTarget as HTMLDetailsElement
  if (!details.open || viewedConfirmationSectionIds.value.includes(sectionId)) return
  viewedConfirmationSectionIds.value = [...viewedConfirmationSectionIds.value, sectionId]
}

function selectPaymentFile(event: Event) {
  const input = event.target as HTMLInputElement
  paymentFile.value = input.files?.[0] || null
  uploadMessage.value = ''
}

async function createSurveyBooking() {
  if (!order.value || !canCreateSurveyBooking.value || bookingSubmitting.value) return
  bookingSubmitting.value = true
  bookingError.value = ''
  bookingMessage.value = ''
  try {
    await api.createSurveyBooking(order.value.id, {
      province: surveyBooking.value.province.trim(),
      city: surveyBooking.value.city.trim(),
      district: surveyBooking.value.district.trim(),
      detail: surveyBooking.value.detail.trim(),
      requestedSlot: surveyBooking.value.requestedSlot.trim() || undefined,
    })
    bookingMessage.value = '量房预约已创建，量房服务费付款节点已经生成。'
    await loadOrder()
    activeSection.value = 'survey'
  } catch (cause) {
    bookingError.value = cause instanceof Error ? cause.message : '量房预约提交失败'
  } finally {
    bookingSubmitting.value = false
  }
}

async function uploadEvidence() {
  if (
    !order.value ||
    !pendingMilestone.value ||
    !payerName.value.trim() ||
    Number(paymentAmountYuan.value) <= 0
  ) {
    actionError.value = '请完整填写付款人和付款金额。'
    return
  }
  uploading.value = true
  actionError.value = ''
  try {
    await api.uploadPaymentEvidence(order.value.id, {
      milestoneId: pendingMilestone.value.id,
      payerName: payerName.value.trim(),
      bankReference: bankReference.value.trim(),
      amountCents: Math.round(Number(paymentAmountYuan.value) * 100),
      attachmentName: paymentFile.value?.name,
    })
    uploadMessage.value = '付款凭证已提交，正在等待财务核销。'
    await loadOrder()
    activeSection.value = 'payment'
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : '凭证上传失败'
  } finally {
    uploading.value = false
  }
}

async function sendConfirmationCode() {
  if (
    !order.value?.confirmation.packageNo ||
    !hasReviewedAllConfirmationSections.value ||
    confirmationSending.value
  ) return
  confirmationSending.value = true
  actionError.value = ''
  confirmationSmsMessage.value = ''
  try {
    const result = await api.sendConfirmationSms(order.value.confirmation.packageNo)
    confirmationCodeSent.value = true
    confirmationSmsMessage.value = result.demoCode
      ? `开发环境验证码：${result.demoCode}`
      : '验证码已经发送到登录手机号。'
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : '验证码发送失败'
  } finally {
    confirmationSending.value = false
  }
}

async function confirmPackage() {
  if (!order.value?.confirmation.packageNo || !canConfirm.value) return
  confirming.value = true
  actionError.value = ''
  try {
    const result = await api.confirmPackage(
      order.value.confirmation.packageNo,
      confirmationCode.value,
    )
    confirmMessage.value = `已于 ${new Date(result.confirmedAt).toLocaleString('zh-CN')} 完成确认。`
    await loadOrder()
    activeSection.value = 'confirmation'
  } catch (cause) {
    actionError.value = cause instanceof ApiError ? cause.message : '确认失败，请重试。'
  } finally {
    confirming.value = false
  }
}

watch(detailTabsRef, observeDetailTabs, { flush: 'post' })

onMounted(() => {
  window.addEventListener('resize', updateDetailTabsScrollability)
  void loadOrder()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateDetailTabsScrollability)
  detailTabsResizeObserver?.disconnect()
})

watch(
  () => route.params.id,
  () => {
    activeSection.value = 'timeline'
    void loadOrder()
  },
)
</script>

<template>
  <section class="section section--order-detail">
    <div class="container">
      <RouterLink class="back-link" to="/orders">
        <ArrowLeftIcon :size="18" aria-hidden="true" />
        返回订单列表
      </RouterLink>

      <StatusState v-if="loading" kind="loading" />
      <StatusState
        v-else-if="error"
        kind="error"
        title="订单暂时无法显示"
        :message="error"
        @retry="loadOrder"
      />
      <template v-else-if="order">
        <div class="order-detail-header">
          <div>
            <span>{{ order.orderNo }}</span>
            <h1>{{ order.productName }}</h1>
            <p>{{ order.city }}，最近更新 {{ order.updatedAt }}</p>
          </div>
          <div class="order-stage">
            <span>当前节点</span>
            <strong>{{ order.stageLabel }}</strong>
          </div>
        </div>

        <div class="next-action-panel">
          <WarningCircleIcon :size="24" weight="fill" aria-hidden="true" />
          <div>
            <strong>现在需要做什么</strong>
            <p>{{ order.nextAction }}</p>
          </div>
        </div>

        <nav ref="detailTabsRef" class="detail-tabs" aria-label="订单详情导航">
          <button :class="{ active: activeSection === 'timeline' }" @click="activeSection = 'timeline'">
            进度时间线
          </button>
          <button :class="{ active: activeSection === 'survey' }" @click="activeSection = 'survey'">
            量房资料
          </button>
          <button
            :class="{ active: activeSection === 'confirmation' }"
            @click="activeSection = 'confirmation'"
          >
            确认资料
          </button>
          <button :class="{ active: activeSection === 'payment' }" @click="activeSection = 'payment'">
            付款记录
          </button>
        </nav>
        <p v-if="detailTabsScrollable" class="detail-tabs__hint">
          ← 左右滑动查看全部标签 →
        </p>

        <div v-if="activeSection === 'timeline'" class="detail-panel">
          <div class="detail-panel__heading">
            <h2>订单进度</h2>
            <p>完成的节点保留时间和说明，未开始的节点只展示预计动作。</p>
          </div>
          <OrderTimeline :events="order.timeline" />
          <div v-if="order.freeze" class="survey-summary">
            <ShieldCheckIcon :size="30" weight="fill" aria-hidden="true" />
            <div>
              <strong>不可变生产资料包</strong>
              <dl>
                <div><dt>冻结编号</dt><dd>{{ order.freeze.number }}</dd></div>
                <div><dt>冻结时间</dt><dd>{{ order.freeze.frozenAt }}</dd></div>
                <div><dt>快照摘要</dt><dd>{{ order.freeze.snapshotHash }}</dd></div>
              </dl>
            </div>
          </div>
        </div>

        <div v-else-if="activeSection === 'survey'" class="detail-panel">
          <div class="detail-panel__heading">
            <h2>专业量房</h2>
            <p>报告提交新版本时，旧版本不会被覆盖。</p>
          </div>
          <p v-if="bookingMessage" class="form-success" role="status">{{ bookingMessage }}</p>
          <div v-if="order.survey.status === 'completed'" class="survey-summary">
            <CheckCircleIcon :size="30" weight="fill" aria-hidden="true" />
            <div>
              <strong>量房报告已完成</strong>
              <dl>
                <div><dt>预约时间</dt><dd>{{ order.survey.appointment }}</dd></div>
                <div><dt>量房人员</dt><dd>{{ order.survey.surveyor }}</dd></div>
                <div><dt>报告版本</dt><dd>{{ order.survey.reportVersion }}</dd></div>
              </dl>
            </div>
          </div>
          <form
            v-else-if="canCreateSurveyBooking"
            class="survey-booking-form"
            @submit.prevent="createSurveyBooking"
          >
            <div class="survey-booking-form__intro">
              <CalendarBlankIcon :size="28" weight="duotone" aria-hidden="true" />
              <div>
                <h3>预约上门量房</h3>
                <p>填写实际量房地址和期望时段。提交后会生成量房服务费付款节点。</p>
              </div>
            </div>
            <div class="survey-address-grid">
              <label>
                <span>省份</span>
                <input
                  v-model="surveyBooking.province"
                  required
                  autocomplete="address-level1"
                />
              </label>
              <label>
                <span>城市</span>
                <input v-model="surveyBooking.city" required autocomplete="address-level2" />
              </label>
              <label>
                <span>区县</span>
                <input
                  v-model="surveyBooking.district"
                  required
                  autocomplete="address-level3"
                />
              </label>
            </div>
            <label>
              <span>详细地址</span>
              <input
                v-model="surveyBooking.detail"
                required
                autocomplete="street-address"
                placeholder="小区、楼栋、单元和门牌号"
              />
              <small>详细地址仅用于订单履约，不会在公开页面展示。</small>
            </label>
            <label>
              <span>期望上门日期（选填）</span>
              <input
                v-model="surveyBooking.requestedSlot"
                type="date"
              />
              <small>具体上门时段由工作人员和量房人员再次确认。</small>
            </label>
            <p v-if="bookingError" class="form-error" role="alert">{{ bookingError }}</p>
            <button class="button button--primary" type="submit" :disabled="bookingSubmitting">
              {{ bookingSubmitting ? '正在创建预约' : '提交量房预约' }}
            </button>
          </form>
          <div v-else class="survey-summary survey-summary--pending">
            <CalendarBlankIcon :size="30" weight="duotone" aria-hidden="true" />
            <div>
              <strong>
                {{
                  order.survey.hasBooking
                    ? order.survey.status === 'booked'
                      ? '已预约上门时间'
                      : '量房预约已创建'
                    : '询价审核通过后可预约'
                }}
              </strong>
              <p v-if="order.survey.hasBooking">
                量房服务费付款节点已生成，财务核销后工作人员会确认上门时间。
              </p>
              <p v-else>当前询价仍在可接单审核中，请关注订单状态更新。</p>
              <button
                v-if="order.survey.hasBooking"
                class="button button--secondary"
                type="button"
                @click="activeSection = 'payment'"
              >
                查看付款节点
              </button>
            </div>
          </div>
        </div>

        <div v-else-if="activeSection === 'confirmation'" class="detail-panel">
          <div class="detail-panel__heading">
            <h2>生产前确认资料</h2>
            <p>请逐项核对。任何关键内容修改后，当前确认都会失效。</p>
          </div>
          <StatusState
            v-if="order.confirmation.sections.length === 0"
            kind="empty"
            title="确认资料正在准备"
            message="量房、方案和正式报价完成后会在这里展示。"
          />
          <template v-else>
            <div class="confirmation-sections">
              <details
                v-for="section in order.confirmation.sections"
                :key="section.id"
                @toggle="markConfirmationSectionViewed(section.id, $event)"
              >
                <summary>
                  <span>{{ section.title }}</span>
                  <CheckCircleIcon :size="20" weight="fill" aria-label="资料已就绪" />
                </summary>
                <dl>
                  <div v-for="item in section.items" :key="item.label">
                    <dt>{{ item.label }}</dt>
                    <dd>{{ item.value }}</dd>
                  </div>
                </dl>
              </details>
            </div>
            <div v-if="order.confirmation.status === 'waiting'" class="confirmation-action">
              <ShieldCheckIcon :size="28" weight="duotone" aria-hidden="true" />
              <div>
                <h3>确认四部分资料</h3>
                <p>验证码会发送到登录手机号 {{ session.maskedPhone }}，验证后写入确认记录。</p>
                <p role="status">
                  请先展开并阅读全部 4 部分内容（已查看 {{ viewedConfirmationSectionCount }}/4）
                </p>
                <button
                  class="button button--secondary"
                  type="button"
                  :disabled="confirmationSending || !hasReviewedAllConfirmationSections"
                  @click="sendConfirmationCode"
                >
                  {{ confirmationSending ? '正在发送' : confirmationCodeSent ? '重新发送验证码' : '获取确认验证码' }}
                </button>
                <p v-if="confirmationSmsMessage" class="form-success" role="status">
                  {{ confirmationSmsMessage }}
                </p>
                <label>
                  <span>短信验证码</span>
                  <input
                    v-model="confirmationCode"
                    type="text"
                    inputmode="numeric"
                    maxlength="6"
                    placeholder="请输入6位验证码"
                  />
                </label>
                <p v-if="actionError" class="form-error" role="alert">{{ actionError }}</p>
                <button
                  class="button button--primary"
                  type="button"
                  :disabled="!canConfirm || confirming"
                  @click="confirmPackage"
                >
                  {{ confirming ? '正在确认' : '确认资料无误' }}
                </button>
              </div>
            </div>
            <div v-else class="confirmation-complete">
              <CheckCircleIcon :size="25" weight="fill" aria-hidden="true" />
              <div><strong>客户确认已完成</strong><p>{{ confirmMessage || '确认记录已写入订单。' }}</p></div>
            </div>
          </template>
        </div>

        <div v-else class="detail-panel">
          <div class="detail-panel__heading">
            <h2>付款与核销记录</h2>
            <p>上传凭证只表示已经提交，实际到账状态以财务核销为准。</p>
          </div>
          <div class="payment-records">
            <article v-for="payment in order.payments" :key="payment.id">
              <FileTextIcon :size="22" weight="duotone" aria-hidden="true" />
              <div>
                <strong>{{ payment.title }}</strong>
                <span>{{ payment.amountLabel }}</span>
              </div>
              <span class="status-badge">{{ payment.status === 'verified' ? '已核销' : payment.status === 'verifying' ? '核销中' : '待处理' }}</span>
            </article>
          </div>
          <form v-if="pendingMilestone" class="upload-evidence" @submit.prevent="uploadEvidence">
            <div>
              <h3>提交线下转账信息</h3>
              <p>提交后由财务核对公司账户实际到账，系统不会仅凭截图自动放行。</p>
            </div>
            <div class="form-row">
              <label>
                <span>付款节点</span>
                <input :value="pendingMilestone.title" disabled />
              </label>
              <label>
                <span>付款金额（元）</span>
                <input v-model="paymentAmountYuan" type="number" min="0.01" step="0.01" required />
              </label>
            </div>
            <div class="form-row">
              <label>
                <span>付款人姓名</span>
                <input v-model="payerName" autocomplete="name" required />
              </label>
              <label>
                <span>银行流水号（选填）</span>
                <input v-model="bankReference" placeholder="请填写转账流水号" />
                <small>可上传银行转账截图代替</small>
              </label>
            </div>
            <label class="file-picker">
              <UploadSimpleIcon :size="21" aria-hidden="true" />
              <span>{{ paymentFile?.name || '选择付款凭证（选填）' }}</span>
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf" @change="selectPaymentFile" />
            </label>
            <p v-if="uploadMessage" class="form-success" role="status">{{ uploadMessage }}</p>
            <p v-if="actionError" class="form-error" role="alert">{{ actionError }}</p>
            <button class="button button--secondary" type="submit" :disabled="uploading">
              {{ uploading ? '正在上传' : '提交财务核销' }}
            </button>
          </form>
          <p v-else class="form-success payment-empty">当前暂无待提交的付款节点</p>
        </div>
      </template>
    </div>
  </section>
</template>
